import React, { useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import EngineeringIcon from "@mui/icons-material/Engineering";
import CloseIcon from "@mui/icons-material/Close";
import ContractorProfileContent from "../../../../components/ContractorProfileContent";
import { formatServiceLevelSummary, getServiceLabel, getServiceLevelLabel } from "../../../../libs/labels";
import { contractorStatusColor, contractorStatusLabel, isContractorReviewPending } from "../../../../libs/contractorReview";

const Contractors = ({
  busyById = {},
  loading,
  contractors = [],
  formatCurrency,
  contractorProfilesByUid = {},
  onReviewContractor,
  title = "Contractors",
  description = "",
  showPendingChip = true,
  reviewEnabled = true,
}) => {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [selectedContractor, setSelectedContractor] = useState(null);
  const [reviewMessage, setReviewMessage] = useState("");
  const [reviewNotes, setReviewNotes] = useState("");
  const [interviewStatus, setInterviewStatus] = useState("not_required");

  const resolveContractorName = (u) => {
    const fromFlat =
      `${u?.fname || u?.first_name || ""} ${u?.lname || u?.last_name || ""}`.trim();
    const fromIndividual =
      `${u?.individual?.first_name || ""} ${u?.individual?.last_name || ""}`.trim();
    const displayName = String(u?.displayName || "").trim();
    return fromFlat || fromIndividual || displayName || "Contractor";
  };

  const filteredRows = useMemo(() => {
    const q = String(search || "").trim().toLowerCase();
    const rows = q ? contractors.filter((u) => {
      const displayName = resolveContractorName(u);
      const service = getServiceLabel(u.metadata?.service);
      const level = getServiceLevelLabel(u.metadata?.service, u.metadata?.level);
      const email = String(u.email || "");
      return [displayName, service, level, email, contractorStatusLabel(u)].join(" ").toLowerCase().includes(q);
    }) : contractors;

    return [...rows].sort((a, b) => {
      const aPending = isContractorReviewPending(a) ? 1 : 0;
      const bPending = isContractorReviewPending(b) ? 1 : 0;
      if (aPending !== bPending) return bPending - aPending;
      return resolveContractorName(a).localeCompare(resolveContractorName(b));
    });
  }, [contractors, search]);
  const pendingReviewCount = useMemo(
    () => contractors.filter(isContractorReviewPending).length,
    [contractors]
  );

  const openContractor = (contractor) => {
    setSelectedContractor(contractor);
    setReviewMessage(String(contractor?.review?.public_message || ""));
    setReviewNotes(String(contractor?.review?.notes || ""));
    setInterviewStatus(String(contractor?.review?.interview_status || "not_required"));
  };

  const selectedUid = String(selectedContractor?.uid || "").trim();
  const reviewBusy = !!busyById[selectedUid];
  const selectedReviewStatus = String(
    selectedContractor?.onboarding_status || selectedContractor?.review?.status || ""
  ).toLowerCase();
  const canMakeReviewDecision = ["submitted", "pending_review"].includes(selectedReviewStatus);
  const selectedProfile = selectedContractor
    ? contractorProfilesByUid?.[selectedContractor.uid] || null
    : null;
  const showReviewControls = reviewEnabled && (canMakeReviewDecision || ["changes_requested", "rejected"].includes(selectedReviewStatus));
  const showReviewActions = reviewEnabled && canMakeReviewDecision;

  const handleReview = async (decision) => {
    if (!selectedContractor || !onReviewContractor) return;
    if (!canMakeReviewDecision) return;
    await onReviewContractor(selectedContractor, decision, {
      public_message: reviewMessage,
      notes: reviewNotes,
      interview_required: interviewStatus !== "not_required",
      interview_status: interviewStatus,
    });
    setSelectedContractor(null);
  };

  const rows = useMemo(
    () => filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredRows, page, rowsPerPage]
  );

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: "8px", minHeight: 300, mb: 2, display: "flex", flexDirection: "column" }}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} alignItems={{ xs: "flex-start", sm: "center" }} justifyContent="space-between" sx={{ mb: 1 }}>
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Avatar variant="rounded" sx={{ width: 28, height: 28, bgcolor: "grey.200", color: "grey.700" }}>
            <EngineeringIcon fontSize="small" />
          </Avatar>
          <Box>
            <Typography sx={{ fontWeight: 700 }}>{title}</Typography>
            {!!description && (
              <Typography variant="caption" color="text.secondary">
                {description}
              </Typography>
            )}
          </Box>
        </Stack>
        {showPendingChip && (
          <Chip
            size="small"
            label={`${pendingReviewCount} pending`}
            color={pendingReviewCount > 0 ? "warning" : "default"}
            sx={{ borderRadius: 1, fontWeight: 700 }}
          />
        )}
      </Stack>

      <TextField
        size="small"
        placeholder="Search contractors"
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
          setPage(0);
        }}
        sx={{ mb: 1.25 }}
      />

      {loading ? (
        <Stack spacing={1} sx={{ flexGrow: 1 }}>
          {Array.from({ length: 6 }).map((_, idx) => (
            <Skeleton key={idx} variant="rounded" height={40} />
          ))}
        </Stack>
      ) : filteredRows.length === 0 ? (
        <Box sx={{ flexGrow: 1, minHeight: 160, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Typography variant="body2" color="text.secondary">No contractors found.</Typography>
        </Box>
      ) : (
        <TableContainer sx={{ flexGrow: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Level</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Rate</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((u) => {
                const displayName = resolveContractorName(u);
                const service = getServiceLabel(u.metadata?.service);
                const level = getServiceLevelLabel(u.metadata?.service, u.metadata?.level);
                const rate = Number(u.metadata?.price || 0);
                const currency = u.metadata?.currency || "usd";
                return (
                  <TableRow
                    key={u.uid || u.id}
                    hover
                    onClick={() => openContractor(u)}
                    sx={{ cursor: "pointer", "&:hover": { cursor: "pointer" } }}
                  >
                    <TableCell>{displayName}</TableCell>
                    <TableCell>{service}</TableCell>
                    <TableCell>{level}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={contractorStatusLabel(u)}
                        color={contractorStatusColor(u)}
                        sx={{ borderRadius: 1 }}
                      />
                    </TableCell>
                    <TableCell align="right">{rate > 0 ? `${formatCurrency(rate, currency)}/hr` : "—"}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {filteredRows.length > 0 && (
        <Box sx={{ mt: "auto", pt: 0.5, borderTop: (theme) => `1px solid ${theme.palette.divider}` }}>
          <TablePagination
            component="div"
            count={filteredRows.length}
            page={page}
            onPageChange={(_event, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 20]}
            labelRowsPerPage="Show"
          />
          <Box sx={{ borderTop: (theme) => `1px solid ${theme.palette.divider}` }} />
        </Box>
      )}

      <Dialog
        open={!!selectedContractor}
        onClose={() => setSelectedContractor(null)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle sx={{ pr: 6 }}>
          {selectedContractor
            ? `${resolveContractorName(selectedContractor)}`
            : "Contractor Profile"}
          <IconButton
            size="small"
            aria-label="Close contractor profile"
            onClick={() => setSelectedContractor(null)}
            sx={{ position: "absolute", right: 12, top: 12 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedContractor ? (
            <Stack spacing={2}>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                <Chip
                  size="small"
                  label={contractorStatusLabel(selectedContractor)}
                  color={contractorStatusColor(selectedContractor)}
                  sx={{ borderRadius: 1 }}
                />
                <Chip size="small" label={`Connect: ${selectedContractor.status || "pending"}`} sx={{ borderRadius: 1 }} />
                <Chip size="small" label={`Visibility: ${selectedContractor.visibility || "hidden"}`} sx={{ borderRadius: 1 }} />
                <Chip size="small" label={`Active: ${selectedContractor.account_active ? "Yes" : "No"}`} sx={{ borderRadius: 1 }} />
              </Stack>
              <Typography variant="body2" color="text.secondary">
                {formatServiceLevelSummary(selectedContractor.metadata?.service, selectedContractor.metadata?.level)}
              </Typography>
              <ContractorProfileContent
                profile={selectedProfile}
                emptyMessage="No profile found for this contractor yet."
              />
              {showReviewControls && (
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                    Review decision
                  </Typography>
                  <Stack spacing={1.25}>
                    <TextField
                      label="Public message to contractor"
                      value={reviewMessage}
                      onChange={(e) => setReviewMessage(e.target.value)}
                      multiline
                      minRows={2}
                      fullWidth
                      disabled={!canMakeReviewDecision}
                      helperText="Required when requesting changes or rejecting. Visible to the contractor."
                    />
                    <TextField
                      label="Internal admin notes"
                      value={reviewNotes}
                      onChange={(e) => setReviewNotes(e.target.value)}
                      multiline
                      minRows={2}
                      fullWidth
                      disabled={!canMakeReviewDecision}
                    />
                    <TextField
                      label="Interview status"
                      value={interviewStatus}
                      onChange={(e) => setInterviewStatus(e.target.value)}
                      select
                      fullWidth
                      disabled={!canMakeReviewDecision}
                    >
                      <MenuItem value="not_required">Not required</MenuItem>
                      <MenuItem value="required">Required</MenuItem>
                      <MenuItem value="scheduled">Scheduled</MenuItem>
                      <MenuItem value="completed">Completed</MenuItem>
                      <MenuItem value="waived">Waived</MenuItem>
                    </TextField>
                    {["changes_requested", "rejected"].includes(selectedReviewStatus) && selectedContractor?.review?.public_message && (
                      <Alert severity="info" sx={{ borderRadius: 2 }}>
                        Current contractor message: {selectedContractor.review.public_message}
                      </Alert>
                    )}
                  </Stack>
                </Box>
              )}
            </Stack>
          ) : null}
        </DialogContent>
        {showReviewActions && (
          <DialogActions sx={{ px: 3, py: 2, flexWrap: "wrap", gap: 1 }}>
            <Button
              variant="contained"
              disabled={!selectedContractor || reviewBusy}
              onClick={() => handleReview("approve")}
              sx={{ textTransform: "none", fontWeight: 800 }}
            >
              Approve
            </Button>
            <Button
              color="error"
              variant="outlined"
              disabled={!selectedContractor || reviewBusy}
              onClick={() => handleReview("reject")}
              sx={{ textTransform: "none" }}
            >
              Reject
            </Button>
            <Button
              variant="outlined"
              disabled={!selectedContractor || reviewBusy || !String(reviewMessage || "").trim()}
              onClick={() => handleReview("request_changes")}
              sx={{ textTransform: "none" }}
            >
              Request changes
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </Paper>
  );
};

export default Contractors;
